/**
 * Workspace Configuration Module
 * 
 * Centralized workspace state management.
 * Exposes workspace access state.
 * 
 * Per PHASE_B_WORK_ORDERS.md B6
 */

import { useState, useEffect } from 'react';

export interface WorkspaceState {
  activeWorkspaceId: string | null;
  workspaceLoading: boolean;
  hasAccess: boolean;
}

let cachedWorkspaceState: WorkspaceState = {
  activeWorkspaceId: null,
  workspaceLoading: true,
  hasAccess: false,
};

const listeners = new Set<(state: WorkspaceState) => void>();

function notifyListeners() {
  listeners.forEach((listener) => listener(cachedWorkspaceState));
}

/**
 * Update workspace state
 */
export function setWorkspaceState(state: Partial<WorkspaceState>) {
  cachedWorkspaceState = { ...cachedWorkspaceState, ...state };
  notifyListeners();
}

/**
 * Hook to access workspace state
 */
export function useWorkspaceState(): WorkspaceState {
  const [state, setState] = useState<WorkspaceState>(cachedWorkspaceState);

  useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  return state;
}

/**
 * Get current workspace state synchronously
 */
export function getWorkspaceState(): WorkspaceState {
  return cachedWorkspaceState;
}
